import React from 'react';
import { ArrowLeft, CheckCircle2, Activity, Zap, Shield, Rocket, Terminal } from 'lucide-react';
import LandingNavbar from '../components/layout/LandingNavbar';
import './ChangelogPage.css';

const RELEASES = [
  {
    version: 'v1.4.0',
    date: 'May 2026',
    title: 'Professional URL Routing & AI Upgrades',
    tag: 'Feature',
    content: [
      'Migrated entire web application to React Router DOM for professional, shareable dashboard links.',
      'Refined the AI thermal analysis root-cause detection to correctly correlate memory leaks with UI rendering artifacts.',
      'Added the infinite-scrolling tech-stack marquee to the landing page.',
    ]
  },
  {
    version: 'v1.3.0',
    date: 'April 2026',
    title: 'Automated PDF Executive Summaries',
    tag: 'Enhancement',
    content: [
      'Engineered an AI-driven Executive Summary generator for the PDF export pipeline.',
      'Improved the visual layout of bug reports, removing text overlaps and enforcing full-width content blocks.',
      'Integrated Razorpay API validation logic for admin payment verification.',
    ]
  },
  {
    version: 'v1.2.0',
    date: 'April 2026',
    title: 'Video Analysis Optimization',
    tag: 'Performance',
    content: [
      'Fixed a database connection pool timeout (`Connection terminated due to connection timeout`) during deployment scripts.',
      'Optimized the Android recording pipeline to asynchronously upload immediately upon stopping the test, bypassing the manual feedback screen.',
      'Added the ability for admins to forcefully re-trigger AI analysis on specific bug reports.',
    ]
  },
  {
    version: 'v1.0.0',
    date: 'March 2026',
    title: 'Initial Platform Release',
    tag: 'Launch',
    content: [
      'Launched the BharatQA cloud dashboard with Google OAuth integration.',
      'Released the beta Android testing SDK for crowdsourced QA agents.',
    ]
  }
];

export default function ChangelogPage({ company, onNavigate }) {
  // Use React Router navigation if available, fallback to legacy onNavigate
  const handleBack = () => {
    if (onNavigate) onNavigate('home');
    else window.location.href = '/';
  };

  return (
    <div className="changelog-page">
      <LandingNavbar company={company} onNavigate={onNavigate} view="changelog" />

      <div className="changelog-container">
        <button className="changelog-back" onClick={handleBack} style={{ marginBottom: '32px' }}>
          <ArrowLeft size={16} /> Back to Home
        </button>

        {/* ── System Status ── */}
        <section className="status-section">
          <div className="status-card">
            <div className="status-header">
              <div className="status-pulse-ring">
                <div className="status-pulse-dot"></div>
              </div>
              <h2>All Systems Operational</h2>
            </div>
            
            <div className="status-grid">
              <div className="status-item">
                <div className="si-top">
                  <Activity size={16} /> <span>API Gateway</span>
                </div>
                <div className="si-state operational">99.99% Uptime</div>
              </div>
              <div className="status-item">
                <div className="si-top">
                  <Zap size={16} /> <span>Gemini AI Engine</span>
                </div>
                <div className="si-state operational">Operational</div>
              </div>
              <div className="status-item">
                <div className="si-top">
                  <Shield size={16} /> <span>Video Processing</span>
                </div>
                <div className="si-state operational">Operational</div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Changelog ── */}
        <div className="changelog-header">
          <h1>Changelog</h1>
          <p>New updates, improvements, and fixes for the BharatQA platform.</p>
        </div>

        <div className="timeline">
          <div className="timeline-line"></div>
          
          {RELEASES.map((release, idx) => (
            <div className="timeline-entry" key={idx}>
              <div className="timeline-marker">
                {release.tag === 'Launch' ? <Rocket size={14} /> : <Terminal size={14} />}
              </div>
              <div className="timeline-content glass-card">
                <div className="tc-header">
                  <div className="tc-meta">
                    <span className="tc-version">{release.version}</span>
                    <span className="tc-date">{release.date}</span>
                  </div>
                  <span className={`tc-tag tag-${release.tag.toLowerCase()}`}>{release.tag}</span>
                </div>
                <h3 className="tc-title">{release.title}</h3>
                <ul className="tc-list">
                  {release.content.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
